import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Link2, Check, MessageCircle, Send, Twitter, Facebook, X } from "lucide-react";
import { toast } from "sonner";
import { shareableVideoUrl } from "@/lib/config";
import { useCountry } from "@/components/contexts/CountryContext";

/**
 * Share a video.
 *
 * The link points at our own /video/:id page, not at the /api/r/ redirect. The
 * redirect 302s to youtube.com, so sharing it sent the recipient straight to
 * YouTube without ever seeing TrendingZ. See shareableVideoUrl for why click
 * attribution still works.
 */
export const ShareMenu = ({ video, compact = false }) => {
  const { country } = useCountry();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = shareableVideoUrl(video, country.code);
  const shareText = `${video.title} — trending on TrendingZ`;

  const targets = [
    {
      name: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    },
    {
      name: "Telegram",
      icon: Send,
      href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      name: "X",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      name: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleTrigger = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Native sheet on mobile is a better experience than our own menu.
    if (navigator.share && window.innerWidth < 768) {
      try {
        await navigator.share({ title: video.title, text: shareText, url: shareUrl });
        return;
      } catch {
        // User dismissed the sheet, or the browser refused - fall through to the menu.
      }
    }
    setOpen((v) => !v);
  };

  const items = (
    <>
      {targets.map(({ name, icon: Icon, href }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
          }}
          className="flex items-center gap-3 px-3 py-3 md:py-2 rounded-lg text-sm md:text-xs text-white/80 hover:bg-white/10 active:bg-white/15 transition-colors"
        >
          <Icon className="w-4 h-4 md:w-3.5 md:h-3.5 shrink-0" />
          {name}
        </a>
      ))}
      <button
        onClick={(e) => {
          e.stopPropagation();
          copyLink();
        }}
        className="w-full flex items-center gap-3 px-3 py-3 md:py-2 rounded-lg text-sm md:text-xs text-white/80 hover:bg-white/10 active:bg-white/15 transition-colors"
      >
        {copied ? (
          <Check className="w-4 h-4 md:w-3.5 md:h-3.5 text-emerald-400 shrink-0" />
        ) : (
          <Link2 className="w-4 h-4 md:w-3.5 md:h-3.5 shrink-0" />
        )}
        {copied ? "Copied" : "Copy link"}
      </button>
    </>
  );

  return (
    <div className="relative">
      <button
        onClick={handleTrigger}
        className={`inline-flex items-center gap-1 rounded-md text-white/60 hover:text-orange-400 transition-colors ${
          compact ? "p-1.5" : "px-2 py-1 text-xs"
        }`}
        aria-label="Share video"
        data-testid={`share-btn-${video.id}`}
      >
        <Share2 className={compact ? "w-4 h-4" : "w-3 h-3"} />
        {!compact && "Share"}
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/*
              Fixed, full-viewport backdrop. The menu itself is rendered inside it on
              mobile as a bottom sheet rather than as a dropdown anchored to the
              button: the button sits low and near the left edge of a card, so an
              absolutely-positioned menu was clipped by the card and ran off the
              side of the screen. A sheet cannot be clipped by an ancestor.
            */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            >
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                className="tz-safe-bottom absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-white/10 bg-zinc-900 p-2 pb-4 shadow-2xl md:hidden"
              >
                <div className="flex items-center justify-between px-3 pb-2 pt-1">
                  <p className="text-sm font-medium text-white/90">Share</p>
                  <button
                    onClick={() => setOpen(false)}
                    aria-label="Close share menu"
                    className="rounded-md p-1 text-white/50 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="truncate px-3 pb-2 text-xs text-white/40">{video.title}</p>
                {items}
              </motion.div>
            </motion.div>

            {/* Desktop keeps the anchored dropdown - there is room for it there. */}
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.96 }}
              className="absolute right-0 bottom-full z-[61] mb-2 hidden w-44 rounded-xl border border-white/10 bg-zinc-900/95 p-1.5 shadow-2xl backdrop-blur md:block"
            >
              {items}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
